import { memo } from 'react';
import { Button, Skeleton, StatusMessage } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { TriangleAlert } from 'lucide-react';

/**
 * What every member tab shows before its own content: the removed member (nothing to manage),
 * the loading skeleton and the load error. Renders `children` once none of those applies.
 *
 * @param {object} props
 * @param {string} props.tabId Route segment of the tab (test id).
 * @param {import('lucide-react').LucideIcon} props.icon Icon of the removed state.
 * @param {boolean} props.isRemoved
 * @param {boolean} props.isLoading
 * @param {string|null} props.error
 * @param {() => void} [props.onRetry] Shown with the load error.
 * @param {import('react').ReactNode} props.children
 */
function MemberTabGuard({ tabId, icon, isRemoved, isLoading, error, onRetry, children }) {
  const { t } = useTranslation('people-access');

  if (isRemoved) {
    return (
      <div data-testid={`member-tab-${tabId}`}>
        <StatusMessage icon={icon} title={t('memberTabs.removed.title')} subtitle={t('memberTabs.removed.subtitle')} />
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className="flex flex-col gap-3" data-testid={`member-tab-${tabId}-loading`}>
        <Skeleton height="72px" borderRadius="12px" />
        <Skeleton height="72px" borderRadius="12px" />
      </div>
    );
  }
  if (error) {
    return (
      <div data-testid={`member-tab-${tabId}`} className="flex flex-col items-center gap-3">
        <StatusMessage
          icon={TriangleAlert}
          title={t('memberTabs.loadError')}
          subtitle={t('memberDetail.state.failed.subtitle')}
        />
        {onRetry ? (
          <Button variant="secondary" size="md" onClick={onRetry}>
            {t('memberTabs.retry')}
          </Button>
        ) : null}
      </div>
    );
  }
  return <div data-testid={`member-tab-${tabId}`}>{children}</div>;
}

export default memo(MemberTabGuard);
